import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useRef, useState } from 'react'
import { AuthCard } from '@/components/auth-card'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { TextInput } from '@/components/text-input'
import { PlatformApiError, resetPlatformPassword } from '@/lib/api'
import { friendlyErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'

/**
 * Reset-password landing page. This is where better-auth redirects
 * the user after they click the link in the reset email:
 *
 *     marketing.com/reset-password?token=XYZ
 *
 * The flow:
 *   1. Read `?token=xyz` from the URL search params.
 *   2. If no token → show a "link is invalid" state with a link
 *      back to /forgot-password to request a fresh one.
 *   3. Otherwise render a new-password form. On submit, POST to
 *      `/api/platform/auth/reset-password` with the token and the
 *      new password. Backend validates the token and updates the
 *      user's password.
 *   4. On success → navigate to /sign-in with a "password updated"
 *      flash state (via router search params).
 *
 * Error handling: better-auth returns 400 with `INVALID_TOKEN`
 * when the token is expired, already-used, or malformed. We show
 * that as "this link has expired, request a new one" to avoid
 * leaking which specific failure happened.
 */
export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
  // Validate the query param so the component gets a typed `token`.
  validateSearch: (search: Record<string, unknown>): { token: string } => {
    const token = typeof search.token === 'string' ? search.token : ''
    return { token }
  },
})

function ResetPasswordPage() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Stash navigate in a ref so the success handler doesn't take a
  // dep on its identity — same pattern as the other auth pages.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError("Passwords don't match.")
      return
    }

    setSubmitting(true)
    try {
      await resetPlatformPassword({ token, newPassword: password })
      // Success — send them to sign in with the new password.
      void navigateRef.current({ to: '/sign-in' })
    } catch (err) {
      // 400 INVALID_TOKEN is by far the most common failure here
      // (expired, reused, forged). Map it to a specific recovery
      // hint. Other 400s (password too short etc.) pass through
      // verbatim. 429/5xx/network fall through to the classifier.
      if (err instanceof PlatformApiError) {
        const body = err.body as { code?: string } | null
        if (err.status === 400 && body?.code === 'INVALID_TOKEN') {
          setError('This reset link is invalid or has expired. Request a new one.')
          return
        }
        if (err.status === 400) {
          setError(err.message)
          return
        }
      }
      setError(friendlyErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  // Early exit if the URL was pasted without a token (or mangled
  // by a client that stripped query strings). Don't show the form
  // at all — there's nothing the user can do here without a token.
  if (!token) {
    return (
      <>
        <NavBar />
        <main>
          <AuthCard
            title="Link is incomplete"
            subtitle="This reset link is missing its token."
            footer={
              <span>
                <Link to="/sign-in" className="font-medium text-primary hover:underline">
                  Back to sign in
                </Link>
              </span>
            }
          >
            <p className="text-sm text-muted-foreground">
              If you followed a link from an email, check that you copied the full URL — the token
              is in the query string. You can also request a fresh link below.
            </p>
            <div className="mt-4">
              <Link
                to="/forgot-password"
                className={cn(
                  'inline-flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium',
                  'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                  'hover:opacity-90 transition-opacity',
                )}
              >
                Request a new reset link
              </Link>
            </div>
          </AuthCard>
        </main>
        <Footer />
      </>
    )
  }

  return (
    <>
      <NavBar />
      <main>
        <AuthCard
          title="Choose a new password"
          subtitle="Enter your new password below. You'll be signed out of any other active sessions."
          footer={
            <span>
              <Link to="/sign-in" className="font-medium text-primary hover:underline">
                Back to sign in
              </Link>
            </span>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <TextInput
              label="New password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              hint="At least 8 characters."
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
            <TextInput
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
            />

            {error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting || !password || !confirm}
              className={cn(
                'w-full rounded-lg px-4 py-2.5 text-sm font-medium',
                'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                'hover:opacity-90 transition-opacity',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              {submitting ? 'Updating password…' : 'Update password'}
            </button>
          </form>
        </AuthCard>
      </main>
      <Footer />
    </>
  )
}
